import type { RateCard } from "./ratecard.ts";
import type { PackagingCatalogue } from "./lever2.ts";
import type { Parcel } from "./types.ts";

/** Keep in step with package.json; a test fails if they differ. */
export const ENGINE_VERSION = "0.5.1";

export interface VersionBlock {
  engineVersion: string;
  /** SHA-256 of the cleaned dataset in canonical form */
  datasetHash: string;
  parcelCount: number;
  /** `id@version` of every rate card used */
  rateCards: string[];
  /** `id@version` of the packaging catalogue, when lever 2 ran */
  packagingCatalogue: string | null;
  generatedAt: string;
}

/** JSON with object keys sorted, so the same data always gives the same text. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(",")}}`;
}

/** Hash of the parcels, independent of row order. Uses Web Crypto (browser and Node 20+). */
export async function datasetHash(parcels: Parcel[]): Promise<string> {
  const sorted = [...parcels]
    .map((p) => ({ ...p, items: [...p.items].sort((a, b) => a.sku.localeCompare(b.sku)) }))
    .sort((a, b) =>
      a.orderId === b.orderId ? a.carrierService.localeCompare(b.carrierService) : a.orderId.localeCompare(b.orderId),
    );
  const bytes = new TextEncoder().encode(canonicalJson(sorted));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function makeVersionBlock(
  parcels: Parcel[],
  rateCards: RateCard[],
  catalogue: PackagingCatalogue | null = null,
  generatedAt: string = new Date().toISOString(),
): Promise<VersionBlock> {
  return {
    engineVersion: ENGINE_VERSION,
    datasetHash: await datasetHash(parcels),
    parcelCount: parcels.length,
    rateCards: rateCards.map((c) => `${c.id}@${c.version}`),
    packagingCatalogue: catalogue ? `${catalogue.id}@${catalogue.version}` : null,
    generatedAt,
  };
}
